import { currency } from '@/utils';
import { pickTravelCreditsForBooking } from '@/modules/utils/travelCredits.js';

const TRAVEL_CREDIT_LABELS = {
  TRAVEL_CREDIT: 'Travelmore',
  GIFT_CERTIFICATE: 'Gift certificate',
  LIFETIME_DEPOSIT: 'Lifetime deposit'
};

function travelCreditDisplayLabel(credit) {
  if (!credit) {
    return '';
  }
  return TRAVEL_CREDIT_LABELS[credit.type] || credit.label || credit.type || 'Travel credit';
}

function isDeductedTravelCredit(credit) {
  return credit && credit.applied !== false;
}

// TODO: should some of these functions be placed into components instead?

export default {
  /** get the current step name based on the current step index */
  currentStepName: state => state.steps[state.stepIndex],

  /** step whose spinner content should show while loading - overridden when a transition is already known to be skipping ahead */
  loadingStepName: state => state.loadingStepOverride || state.steps[state.stepIndex],

  /** get entire quote subtotal based on rooms, excursions, donations */
  runningSubtotal: (state, getters) => {
    const roomsSubtotal = state.rooms.reduce((accumulator, room) => {
      if (room.id) {
        return accumulator + (room.pricePerPerson * room.occupancy);
      }
      return accumulator;
    }, 0);

    const excursionsSubtotal = state.excursions.reduce((accumulator, excursion) => {
      return accumulator + (excursion.price * excursion.passengers.filter(passenger => passenger.selected).length);
    }, 0);

    let subtotal = roomsSubtotal + excursionsSubtotal;
    var donatedAmount = 0;

    if (state.donationsData && state.donationsData.donationExists && getters.donationData && getters.donationData.price) {
      if (getters.donationAllocationType == 'PerPerson') {
        // Use sessionStorage as source of truth - nonDonatingTravellers has timing/race issues
        void state.donationUpdateTrigger;
        let donatingCount = 0;
        state.rooms.forEach((room, roomIndex) => {
          if (!room.id || !room.passengers.length) {
            return;
          }
          room.passengers.forEach((_, passengerIndex) => {
            if (sessionStorage.getItem(`room-${roomIndex}-passenger-${passengerIndex}-donation`) === 'true') {
              donatingCount++;
            }
          });
        });
        donatedAmount = donatingCount * getters.donationData.price;
      }

      if (getters.donationAllocationType == 'PerBooking') {
        donatedAmount = state.donationPerBooking === true ? getters.donationData.price : 0;
      }
    }

    return subtotal + donatedAmount;
  },

  selectedLeadTravelCredit: state => {
    const selected = pickTravelCreditsForBooking(state.validatedTravelCredits);
    if (!selected.length) {
      return null;
    }
    return {
      ...selected[0],
      label: travelCreditDisplayLabel(selected[0]),
      amount: selected.reduce((total, credit) => total + Number(credit.amount), 0)
    };
  },

  travelCreditBreakdownLines: (state, getters) => {
    const applied = (state.appliedTravelCredits || []).filter(isDeductedTravelCredit);
    if (!applied.length) {
      // Before the booking exists, show the signed-in lead's live-validated credit as pending.
      const pending = !state.bookingId && getters.selectedLeadTravelCredit;
      return pending
        ? [{ label: pending.label, count: 1, amount: pending.amount, pending: true }]
        : [];
    }

    const groups = {};
    applied.forEach(credit => {
      const label = credit.label || travelCreditDisplayLabel(credit);
      if (!groups[label]) {
        groups[label] = { label, count: 0, amount: 0 };
      }
      groups[label].count += 1;
      groups[label].amount += Number(credit.amount || 0);
    });
    return Object.values(groups);
  },

  /** every credit we know about, applied or not - the table marks which is which */
  paymentTravelCredits: state => state.appliedTravelCredits || [],

  /** does the tour have addons available */
  hasAddons: state => state.addonData && state.addonData.addOns && state.addonData.addOns.length,

  /** does the quote have an addon selected (the initial tour code will be different) */
  addonSelected: state => state.quote.initialTourCode !== state.quote.tourCode,

  /** get a room type by that room id */
  availableRoomById: state => id => state.availableRooms.find(room => room.id === id),

  /** check if we can add another room, based on the current room selection, passengers and max passengers */
  canAddRoom: (state, getters) => {
    // disallow if one room section does not have a selected room
    if (!state.rooms.every(room => room.id)) {
      return false;
    }
    return getters.roomPassengers.length < state.maxPassengers;
  },

  /** get the remaining room types for each room based on the remaining passenger allowance */
  remainingAvailableRoomsForRoom: (state, getters) => roomIndex => {
    // get other rooms
    const otherRooms = state.rooms.filter((room, index) => index !== roomIndex);

    // get total passengers in other rooms
    const otherRoomsPassengerCount = getters.roomsPassengerCount(otherRooms);

    // get remaing passenger allowance for this room
    const remainingPassengerAllowance = state.maxPassengers - otherRoomsPassengerCount;

    // return array of available rooms based on occupancy
    return state.availableRooms.filter(room => room.occupancy <= remainingPassengerAllowance);
  },

  /** build up a passenger array from each rooms passengers */
  roomPassengers: (state, getters, rootState, rootGetters) => {
    void state.roomPassengersCacheBuster;
    
    const roomPassengers = [];

    let isLead = -1;

    state.rooms.forEach((room, roomIndex) => {
      room.passengers.forEach((passenger, passengerIndex) => {
        const passengerData = rootGetters.formDataFlat(passenger);

        const sessionFirstName = sessionStorage.getItem(`room-${roomIndex}-passenger-${passengerIndex}-firstName`);
        const sessionLastName = sessionStorage.getItem(`room-${roomIndex}-passenger-${passengerIndex}-lastName`);
        const sessionEmail = sessionStorage.getItem(`room-${roomIndex}-passenger-${passengerIndex}-email`);

        passengerData.firstName = sessionFirstName !== null ? sessionFirstName : passengerData.firstName || '';
        passengerData.lastName = sessionLastName !== null ? sessionLastName : passengerData.lastName || '';
        passengerData.email = sessionEmail !== null ? sessionEmail : passengerData.email || '';

        // check if we have passengerDetails
        let passengerDetails = {};
        if (passenger.passengerId) {
          passengerDetails = getters.passengersDetails.find(p => p.passengerId === passenger.passengerId);
        }

        roomPassengers.push({
          ...passengerDetails,
          ...passengerData,
          passengerGuid: passenger.key,
          roomId: room.id,
          roomKey: room.key,
          roomCode: room.roomCode ?? '',
          lead: !++isLead
        });
      });
    });

    return roomPassengers;
  },

  productId: (state) => {
    return state.data.productId;
  },

  departureId: (state) => {
    return state.data.departureId;
  },

  nonDonatingTravellers: (state) => {
    return state.nonDonatingTravellers;
  },

  donatingTravellers: (state, getters, rootState, rootGetters) => {
    if (!state.donationsData.donationExists)
      return [];

    let donatingTravellers = [];

    getters.roomPassengers.forEach(passenger => {
      const passangerName = rootGetters.passengerFullName(passenger);
      const label = (passangerName || '').trim();
      if (!label) {
        return;
      }

      if (!state.nonDonatingTravellers.includes(label)) {
        donatingTravellers.push(label);
      }
    });

    return donatingTravellers;
  },

  donatingPassengerGuids: (state, getters, rootState, rootGetters) => {
    if (!state.donationsData.donationExists)
      return [];

    let donatingPassengerGuids = [];

    getters.roomPassengers.forEach(passenger => {
      const passangerName = rootGetters.passengerFullName(passenger);
      const label = (passangerName || '').trim();
      if (!label) {
        return;
      }

      if (!state.nonDonatingTravellers.includes(label)) {
        donatingPassengerGuids.push(passenger.passengerGuid);
      }
    });

    return donatingPassengerGuids;
  },

  donationExtraId: (state) => {
    return state.donationsData.id;
  },

  donationFeeId: (state) => {
    return state.donationsData.feeId;
  },

  donationFeePrice: (state) => {
    return state.donationsData.price;
  },

  donationAllocationType: (state) => {
    return state.donationsData.allocationType;
  },

  donationData: (state) => {
    return state.donationsData;
  },

  donationPerBooking: (state) => {
    return state.donationPerBooking;
  },

  donated: (state) => {
    return state.donationMade;
  },

  passangersWhoDonated: (state) => {
    return state.quote.passengers.filter(p => p.donated);
  },

  // TODO: should we make this a function rather than a getter?
  /** get total count of passengers from array of rooms */
  roomsPassengerCount: () => rooms => rooms.reduce((accumulator, room) => {
    return accumulator + (room.occupancy || 0);
  }, 0),

  // TODO: should we make this a function rather than a getter?
  /** get the global passenger index taking rooms before into account */
  globalPassengerIndex: (state, getters) => (roomIndex, passengerIndex) => {
    const roomsBefore = state.rooms.slice(0, roomIndex);
    const roomsBeforePassengerCount = getters.roomsPassengerCount(roomsBefore);
    return roomsBeforePassengerCount + passengerIndex + 1;
  },

  /** get currency formatted for correct country and currency */
  currency: state => amount => currency(amount, state.quote.cultureCode, state.quote.currency),

  /** get excursions and passenger data in minimal format */
  excursions: (state) => {
    return state.excursions.map(excursion => ({
      code: String(excursion.activitiesId),
      passengers: excursion.passengers.filter(passenger => passenger.selected).map(passenger => passenger.firstName + ' ' + passenger.lastName)
    })).filter(excursion => excursion.passengers.length);
  },

  excursionsPassengerGuids: (state) => {
    return state.excursions.map(excursion => ({
      code: String(excursion.activitiesId),
      name: String(excursion.name),
      passengerGuids: excursion.passengers.filter(passenger => passenger.selected).map(passenger => passenger.passengerGuid)
    })).filter(excursion => excursion.passengerGuids.length);
  },

  /** check if a passenger is going on an excursion */
  passengerHasExcursion: (state, getters) => (passenger, excursion) => {
    const theExcursion = getters.excursionsPassengerGuids.find(e => e.code === String(excursion.activitiesId || excursion.id));
    
    if (theExcursion) {
      const hasExcursion = theExcursion.passengerGuids.indexOf(passenger.passengerGuid) > -1;
      return hasExcursion;
    }
    return false;
  },

  /** get the first incomplete passenger */
  firstIncompletePassenger: (state, getters, rootState, rootGetters) => {
    return state.passengers.find(passenger => {
      return !passenger.hasBeenSubmitted || !rootGetters.formIsValid(passenger) || !passenger.summaryView;
    });
  },

  allPassengersComplete: (state, getters) => {
    return !getters.firstIncompletePassenger;
  },

  /** check if a passenger is editable */
  passengerIsEditable: (state, getters, rootState, rootGetters) => passenger => {
    // if the passenger is the next incomplete passenger
    if (getters.firstIncompletePassenger && getters.firstIncompletePassenger.id === passenger.id) {
      return true;
    }

    // if the passenger is complete and summary view
    if (passenger.summaryView && rootGetters.formIsValid(passenger)) {
      return true;
    }

    return false;
  },

  /** get all passenger data for sending to API */
  passengersDetails: (state, getters, rootState, rootGetters) => {
    return state.passengers.map(passenger => {
      const passengerData = {
        passengerId: passenger.id,
        passengerGuid: passenger.passengerGuid,
        lead: passenger.lead,
        roomId: passenger.roomId,
        ...rootGetters.formDataFlat(passenger)
      };

      if (passengerData.address?.addressLines) {
        passengerData.address.addressLines = Object.values(passengerData.address.addressLines);
      }

      return passengerData;
    });
  },

  passengerHasDuplicateName: () => passengerArray => {

    // Add each full name to an array
    const getPassengers = passengerArray
      .map(passenger => `${passenger.firstName || ''} ${passenger.lastName || ''}`)
      .filter(passenger => Boolean(passenger.trim()));

    // nothing has been filled out yet
    if (!getPassengers.length) {
      return false;
    }

    // create a unique passenger set from original array
    let uniquePassengers = new Set(getPassengers);
    uniquePassengers = [...uniquePassengers];

    // compare length of unique passengers array and original passenger array
    if (getPassengers.length === uniquePassengers.length) {
      return false;
    } else {
      // if they are different then there are duplicates (and fail)
      return true;
    }

  },

  /**
   * guids of passengers whose full name repeats an earlier passenger's, for per-field error
   * display. Only the later occurrence is flagged (not the first passenger to use that name),
   * since a repeated last name alone is normal (e.g. family members) — the border is meant to
   * point at "the one you just typed that collides", not every passenger sharing the name.
   */
  duplicatePassengerGuids: (state, getters) => {
    const seenNames = new Set();
    const guids = new Set();

    getters.roomPassengers.forEach(passenger => {
      const name = `${passenger.firstName || ''} ${passenger.lastName || ''}`.trim().toLowerCase();
      if (!name) return;
      if (seenNames.has(name)) {
        guids.add(passenger.passengerGuid);
      } else {
        seenNames.add(name);
      }
    });

    return guids;
  },

  /** check if at least one passenger has a medical condition */
  passengersHaveMobilityLimitations: (state, getters) => {
    return getters.passengersDetails.some(passenger => passenger.mobilityIssues || passenger.medicalConditions);
  },

  /** get payment data to send to API */
  paymentData: (state, getters, rootState, rootGetters) => {
    return {
      ...getters.reservationData,
      ...rootGetters.formDataFlat(state.payment)
    }
  },

  /** check if the payment form is valid */
  paymentFormIsValid: (state, getters, rootState, rootGetters) => {
    return (
      rootGetters.formIsValid(['checkout', 'payment'])
    );
  },

  /** checkout is reservation if cannot pay or mobility limitations */
  isReservation: (state, getters) => {
    const cannotPay = state.bookingData && state.payment && !state.payment.canPay;
    const hasMobility = getters.passengersHaveMobilityLimitations;
    const withinTwelveWeeks = getters.isDepartureWithinTwelveWeeks;

    return withinTwelveWeeks && hasMobility || cannotPay;
  },

  /** get reservation data to send to API */
  reservationData: state => {
    return {
      quoteId: state.quote.quoteId,
      currentPageUrl: window.location.origin + window.location.pathname
    }
  },

  totalDeposit: state => {
    return state.departureServices.reduce((sum, service) => {
      return sum + (service.deposit || 0);
    }, 0);
  },

  takeFinalBalance: (state) => {
    return state.takeFinalBalance;
  },

  depositPaymentAllowed: (state) => {
    const today = new Date().toISOString().split('T')[0];
    return today < state.bookingData.balanceDueDate;
  },

  isDepartureWithinTwelveWeeks: state => {
    const balanceDueDate = state.bookingData.balanceDueDate;
    const today = new Date().toISOString().split('T')[0];

    return today >= balanceDueDate;
  },

  shouldShowError(state, getters) {
    return getters.passengersHaveMobilityLimitations ? getters.isDepartureWithinTwelveWeeks : false;
  }
}
